import cuid from "cuid";
import { filter, find, get, isEmpty, map } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
} from "utils/api/graphql/mutations/attachment";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  ATTACHMENT_TYPES,
  DOCUMENTS_TYPES,
  KEYS,
  PROCESS_TYPES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

export const DOCUMENTS_TYPE = {
  cin: DOCUMENTS_TYPES.CIN,
  paymentAccount: DOCUMENTS_TYPES.PAYMENT_ACCOUNT,
  professionalPaymentAccount: DOCUMENTS_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  enrollmentCertificate: DOCUMENTS_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE,
  socialSecurityCertificate: DOCUMENTS_TYPES.SOCIAL_SECURITY_CERTIFICATE,
  paySlip: DOCUMENTS_TYPES.PAY_SLIP,
  electronicSignatureEligibility:
    DOCUMENTS_TYPES.ELECTRONIC_SIGNATURE_ELIGIBILITY,
  supplementaryDocuments: ATTACHMENT_TYPES.OTHER,
};

const Justification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState(false);
  const [, setLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.JUSTIFICATION", { returnObjects: true });
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);

  const supportingDocuments = useMemo(() => {
    const profile = get(data, "subscription.project.contact.fields.profile");
    return get(
      find(
        get(data, "subscription.insurancePlan.fields.supportingDocuments"),
        ({ profiles, processType }) =>
          processType === PROCESS_TYPES.B2C && profiles.includes(profile),
      ),
      "attachments",
      [],
    );
  }, [data]);

  const adherentUserId =
    get(data, "subscription.project.contact.user.id") ||
    get(project, "contact.user.id");

  const { loading: attachmentLoading, refetch: refetchAttachments } = useQuery(
    graphql.queries.ATTACHMENTS,
    {
      fetchPolicy: "no-cache",
      variables: {
        where: {
          AND: [
            {
              subscription: { id: subscriptionId },
            },
            {
              user: { id: adherentUserId },
            },
          ],
        },
        isIn: {
          type: [...Object.values(DOCUMENTS_TYPE), ATTACHMENT_TYPES.OTHER],
        },
      },
      skip: !adherentUserId,
      onCompleted: ({ attachments: { data: attachmentsList } }) => {
        setInitialValues({
          cin: map(
            filter(
              attachmentsList,
              ({ type }) => type === ATTACHMENT_TYPES.CIN,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          paymentAccount: map(
            filter(
              attachmentsList,
              ({ type }) => type === ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          professionalPaymentAccount: map(
            filter(
              attachmentsList,
              ({ type }) =>
                type === ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          enrollmentCertificate: map(
            filter(
              attachmentsList,
              ({ type }) =>
                type === ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          socialSecurityCertificate: map(
            filter(
              attachmentsList,
              ({ type }) =>
                type === ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          electronicSignatureEligibility: map(
            filter(
              attachmentsList,
              ({ type }) =>
                type === ATTACHMENT_TYPES.ELECTRONIC_SIGNATURE_ELIGIBILITY,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          paySlip: map(
            filter(
              attachmentsList,
              ({ type }) => type === ATTACHMENT_TYPES.PAY_SLIP,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          supplementaryDocuments: map(
            filter(
              attachmentsList,
              ({ type }) => type === ATTACHMENT_TYPES.OTHER,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
        });
        setLoading(false);
      },
    },
  );
  const [addAttachment] = useMutation(CREATE_ATTACHMENT);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  const onSubmit = async (documents) => {
    setLoading(true);
    const documentsToUpload = [];
    if (!adherentUserId) {
      setLoading(false);
      return;
    }

    const uploadedKeys = {};
    Object.entries(documents)?.map(([key, attachments]) =>
      attachments
        ?.filter((file) => {
          if (!file || file.status) return false;
          const uploadKey = file.uid || `${key}:${file.name}:${file.size}`;
          if (uploadedKeys[uploadKey]) return false;
          uploadedKeys[uploadKey] = true;
          return true;
        })
        .forEach(({ name, contentType, base64 }) => {
          documentsToUpload.push({
            id: cuid(),
            subscription: { id: subscriptionId },
            user: { id: adherentUserId },
            name,
            content: base64,
            contentType,
            type: DOCUMENTS_TYPE[key],
          });
        }),
    );
    await Promise.all(
      documentsToUpload.map((document, index) =>
        addAttachment({
          variables: {
            data: document,
          },
        }),
      ),
    );
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });

    onNext({
      payload: {},
      actionsTemplate: { subscriptionId },
    });
  };
  const downloadFile = async (id) => {
    await downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
      },
    });
  };

  const handleFileRemove = (fieldName) => async (item) => {
    const fileId = item?.uid || item?.id;

    // If file is already uploaded (has an id and doesn't contain "AUTO"), delete it
    if (fileId && typeof fileId === "string" && !fileId.includes("AUTO")) {
      try {
        await deleteAttachment({
          variables: {
            where: { id: fileId },
          },
        });
        // Refetch attachments to update the UI
        if (refetchAttachments) {
          await refetchAttachments();
        }
      } catch (_error) {
        return fieldName;
      }
    }

    // Return the field name so View.js can update the form values
    return fieldName;
  };

  if (attachmentLoading || isEmpty(initialValues) || isEmpty(data))
    return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showProfile={false}
      />
      <View
        onSubmit={onSubmit}
        supportingDocuments={supportingDocuments}
        initialValues={initialValues}
        translate={TRANSLATION}
        downloadFile={downloadFile}
        isRia={get(data, "subscription.project.ria") === true}
        onBack={onBack}
        onFileRemove={handleFileRemove}
      />
    </>
  );
};
export default Justification;
